import React, { type ReactElement, useMemo } from 'react'
import { PrimaryButton } from '../../components/Button'
import { InputLabel, OptionalLabel } from '../../components/Input'
import PageHeader from '../../components/PageHeader'
import { AccountServicesType, BusinessAddress as BusinessAddressState } from '../../state/Radom'
import { highlightInvalidFields, sanitizeAddress } from '../../util/Util'
import Tooltip from '../../components/Tooltip'
import { AddressForm } from './AddressForm'
import PdfFileUpload from './PDFFileUpload'
import { RADOM_COLORS } from '../../util/Constants'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'

interface IProps {
  state: BusinessAddressStateExtended
  isSubmitting: boolean
  onUpdate: (value: BusinessAddressStateExtended) => void
  onSubmit: () => void
  accountType: AccountServicesType
  getError: (fieldId: string) => string | undefined

}

type BusinessAddressStateExtended = BusinessAddressState & {
  proofOfAddressFile?: File
  proofOfAddressUrl?: string
}

const BusinessAddress = ({ state, isSubmitting, onUpdate, onSubmit, accountType, getError }: IProps): ReactElement => {
  const isValid = useMemo(() => ({
    ...(accountType === AccountServicesType.crypto_and_fiat
      ? {
        streetLine: state.physicalAddress?.streetLine1 !== '',
        city: state.physicalAddress?.city !== '',
        country: state.physicalAddress?.country !== '',
        proofOfAddressFile: !!state.proofOfAddressFile || !!state.proofOfAddress
      }
      : {
        streetLine: state.registeredAddress?.streetLine1 !== '',
        city: state.registeredAddress?.city !== '',
        country: state.registeredAddress?.country !== ''
      })
  }), [state, accountType])

  const isFormValid = useMemo(() => Object.values(isValid).every((value) => value), [isValid])
  return (<div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 14, paddingBottom: 40 }}>
    <div style={{ marginBottom: 20 }}>
      <PageHeader title="Tell us where you're located" subTitle="Share your business's registered and physical addresses" />
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <span style={{ fontSize: 20 }}>Registered address</span>
      <VerificationRequirementError
        message={[
          getError('registeredAddress.streetLine1'),
          getError('registeredAddress.streetLine2'),
          getError('registeredAddress.city'),
          getError('registeredAddress.postalCode'),
          getError('registeredAddress.country')
        ]
          .filter(Boolean)
          .join(' ')}
      />
      <AddressForm state={state.registeredAddress} onUpdate={(t) => {
        const sanitizedStreetLine = sanitizeAddress(t.streetLine1 || '')
        const sanitizedStreetLine2 = sanitizeAddress(t.streetLine2 || '')
        const sanitizedCity = sanitizeAddress(t.city || '')
        const sanitizedPostalCode = sanitizeAddress(t.postalCode || '')

        onUpdate({
          ...state,
          registeredAddress:
          {
            ...t,
            streetLine1: sanitizedStreetLine,
            streetLine2:
            sanitizedStreetLine2,
            city: sanitizedCity,
            postalCode: sanitizedPostalCode
          }
        })
      }} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
      <span style={{ fontSize: 20 }}>Operating address</span>
      <VerificationRequirementError
        message={[
          getError('physicalAddress.streetLine1'),
          getError('physicalAddress.streetLine2'),
          getError('physicalAddress.city'),
          getError('physicalAddress.postalCode'),
          getError('physicalAddress.country')
        ]
          .filter(Boolean)
          .join(' ')}
      />
      <AddressForm state={state.physicalAddress} onUpdate={(t) => {
        const sanitizedStreetLine = sanitizeAddress(t.streetLine1 || '')
        const sanitizedStreetLine2 = sanitizeAddress(t.streetLine2 || '')
        const sanitizedCity = sanitizeAddress(t.city || '')
        const sanitizedPostalCode = sanitizeAddress(t.postalCode || '')
        onUpdate({
          ...state,
          physicalAddress:
          {
            ...t,
            streetLine1: sanitizedStreetLine,
            streetLine2:
            sanitizedStreetLine2,
            city: sanitizedCity,
            postalCode: sanitizedPostalCode
          }
        })
      }} />
    </div>

    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
        <InputLabel>
          Proof of address
        </InputLabel>
        <OptionalLabel>PDF</OptionalLabel>
        <Tooltip message='Upload a document proving your operating address, such as a utility bill or bank statement.' />
      </div>
      {getError('proofOfAddress') && <div style={{ marginBottom: 10 }}><VerificationRequirementError message={getError('proofOfAddress')} /></div>}
      <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
        <div style={{
          border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
          padding: 20,
          background: RADOM_COLORS.GRAY9,
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        }}>
          <span>Upload one of the following issued to the applying entity:</span>
          <ul style={{ paddingLeft: 30, margin: 0 }}>
            <li><b>Utility bill</b> (less than 90 days old)</li>
            <li><b>Bank statement</b> (less than 90 days old)</li>
            <li><b>Office lease agreement</b> (may be older than 90 days)</li>
          </ul>
        </div>
        <PdfFileUpload
          file={state.proofOfAddressUrl ?? state.proofOfAddressFile}
          onFile={f => onUpdate({ ...state, proofOfAddressFile: f })}
          onRemove={() => {
            onUpdate({ ...state, proofOfAddressUrl: undefined, proofOfAddressFile: undefined })
          }} />
      </div>
    </div>

    <PrimaryButton
      disabled={isSubmitting}
      isLoading={isSubmitting}
      onClick={() => {
        if (isFormValid) {
          onSubmit()
        } else {
          highlightInvalidFields()
        }
      }}>
      Continue
    </PrimaryButton>
  </div>
  )
}

export default BusinessAddress
